import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { authService } from "../services/authService";
import { useAnalytics } from "../hooks/useAnalytics";
import PageTransition from "../components/PageTransition";
import { SignInPage } from "../components/ui/sign-in";

const SignIn = () => {
  const navigate = useNavigate();
  const { trackEvent, trackButtonClick } = useAnalytics();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check if user is already fully authenticated on component mount
  useEffect(() => {
    const checkAuthState = () => {
      // Use enhanced isLoggedIn() which checks both Firebase auth AND API key
      const isFullyAuthenticated = authService.isLoggedIn();

      if (isFullyAuthenticated) {
        navigate("/dashboard");
      } else {
        // Check detailed auth status for debugging
        const authStatus = authService.getAuthStatus();

        if (authStatus.hasFirebaseAuth && !authStatus.hasApiKey) {
          // User has Firebase auth but missing API key - staying on login page
        }
      }
    };

    checkAuthState();
  }, [navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Track login attempt
    trackEvent('login_attempt', {
      method: 'email',
      email_provided: !!email
    });

    try {
      const result = await authService.signInWithEmail(email, password);
      
      // Track successful login
      trackEvent('login_success_email', {
        method: 'email',
        success: true,
        is_first_login: result.firstLogin === true,
        user_id: result.user?.uid
      });

      // Track button click
      trackButtonClick('sign_in_email', 'signin_page');
      
      if (result.firstLogin === true) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      // Track failed login
      trackEvent('login', {
        method: 'email',
        success: false,
        error_type: error.message === 'auth/invalid-credential' ? 'invalid_credentials' : 'other',
        error_message: error.message
      });

      if (error.message === 'auth/invalid-credential') {
        setError('invalid-credentials');
      } else {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setIsLoading(true);

    // Track Google login attempt
    trackEvent('login_attempt', {
      method: 'google'
    });

    try {
      const result = await authService.signInWithGoogle();
      
      // Track successful Google login
      trackEvent('login_success_google', {
        method: 'google',
        success: true,
        is_first_login: result.firstLogin === true,
        user_id: result.user?.uid
      });

      // Track button click
      trackButtonClick('sign_in_google', 'signin_page');
      
      if (result.firstLogin === true) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      // Track failed Google login
      trackEvent('login', {
        method: 'google',
        success: false,
        error_type: error.message.includes('cancelled') ? 'user_cancelled' : 
                   error.message.includes('popup') ? 'popup_blocked' : 'other',
        error_message: error.message
      });

      // Handle specific Firebase errors with custom messages
      if (error.message === 'auth/invalid-credential') {
        setError('invalid-credentials');
      } else {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Sample testimonials for the new UI
  const sampleTestimonials = [
    {
      avatarSrc: "https://randomuser.me/api/portraits/women/57.jpg",
      name: "Sarah Chen",
      handle: "@sarahdigital",
      text: "Amazing platform! The user experience is seamless and the features are exactly what I needed."
    },
    {
      avatarSrc: "https://randomuser.me/api/portraits/men/64.jpg",
      name: "Marcus Johnson",
      handle: "@marcustech",
      text: "This service has transformed how I work. Clean design, powerful features, and excellent support."
    },
    {
      avatarSrc: "https://randomuser.me/api/portraits/men/32.jpg",
      name: "David Martinez",
      handle: "@davidcreates",
      text: "I've tried many platforms, but this one stands out. Intuitive, reliable, and genuinely helpful for productivity."
    },
  ];

  return (
    <PageTransition>
      <SignInPage
        title={<span className="font-light text-foreground tracking-tighter">Welcome back</span>}
        description="Sign in to access your AI agents and automation tools"
        heroImageSrc="https://images.unsplash.com/photo-1642615835477-d303d7dc9ee9?w=2160&q=80"
        testimonials={sampleTestimonials}
        onSignIn={handleSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onResetPassword={() => {
          // TODO: Implement password reset functionality
          console.log("Reset password clicked");
        }}
        onCreateAccount={() => {
          // TODO: Implement create account functionality
          console.log("Create account clicked");
        }}
        email={email}
        password={password}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        isLoading={isLoading}
        error={error}
        showPassword={showPassword}
        onTogglePassword={togglePasswordVisibility}
      />
    </PageTransition>
  );
};

export default SignIn; 